import d3 from "d3";
import R from "ramda";
import $ from "jquery";
import * as core from "../data/core.js";
import {compare, descending} from "../data/compare.js";
import {moveToFront} from "./d3Extensions.js";

export default function createSpreadExpanded(initialState) {

    const state = {
        selector: "",
        width: 0,
        height: null,
        indicators: {},
        locations: {},
        cleanData: {},
        activeIndicator: null,
        onClose: () => {}
    };

    function setState(newState) {
        Object.assign(state, newState);
        update();
    }

    // merge the initial state in with the default state before we build the svg
    Object.assign(state, initialState);

    // constant height for now
    const margin = { top: 20, right: 20, bottom: 50, left: 60 };

    const differenceNoteOffset = 15;

    var vizWidth, vizHeight;

    const svg = d3.select(state.selector).append("svg");

    // close (X) button
    d3.select(state.selector).append("div")
        .attr("class", "close")
        .html("<svg class='svg-x'> <use xlink:href='build/img/svg-sprite.svg#x'></use> </svg>")
        .on("click", closeSpread);


    // clear the active indicator and alert any listeners
    function closeSpread(){
        setState({
            activeIndicator: null
        });
        state.onClose();
    }

    // if the user clicked outside of the spread, close it
    function handleClickAway(e) {
        const inSpread = $(e.target).closest(state.selector).length;
        if (!inSpread){
            e.stopPropagation();
            closeSpread();
        }
    }


    // close the spread when the user presses escape
    function handleKeyPress(e) {
        if (e.keyCode === 27){ closeSpread(); }
    }

    const g = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

    // better worse label
    g.append("text")
        .attr("class", "better-worse");

    const titleContainer = d3.select(state.selector).append("span").attr("class", "title");

    // Arrow heads
    const defs = svg.append("defs");

    defs
        .append("marker")
        .attr("id", "arrow-spread")
        .attr("markerWidth", 10)
        .attr("markerHeight", 10)
        .attr("refX", 0)
        .attr("refY", 3)
        .attr("orient", "auto-start-reverse")
        .attr("markerUnits", "strokeWidth")
        .append("path")
        .attr("d", "M0,0 L0,6 L5,3 z");

    var xScale = d3.scale.ordinal(),
      yScale = d3.scale.linear();

    const valueComparator = compare(core.latestYearValue, descending);


    function xTickFormat(d, i){
        return state.locations[d].name;
    }

    function yTickFormat(d, i){
        return core.formatValue(state.indicators[state.activeIndicator], d, false);
    }

    const xAxis = d3.svg.axis()
      .scale(xScale)
      .tickPadding(13)
      .tickSize(0)
      .tickFormat(xTickFormat);

    const yAxis = d3.svg.axis()
      .scale(yScale)
      .tickPadding(10)
      .orient("left")
      .tickFormat(yTickFormat);

    const differencePath = d3.svg.line()
        .x(d => xScale.rangeExtent()[1] + differenceNoteOffset)
        .y(d => yScale(core.latestYearValue(d)));

    function axisArrowsPath(){
        return `M -5 -5 L -5 ${vizHeight + 5} L ${vizWidth + 5} ${vizHeight + 5}`;
    }

    function isExtreme(locId){
        return locId === R.head(xScale.domain()) || locId === R.last(xScale.domain());
    }

    function positionX(locId){
        if(locId === R.head(xScale.domain())){
            return 0;
        }
        else if(locId === R.last(xScale.domain())){
            return xScale.rangeExtent()[1];
        }
        else{
            return xScale(locId);
        }
    }

    function update() {

        // if there is no active indicator, hide the spread
        if (!state.activeIndicator){
            d3.select(state.selector)
                .style("pointer-events", "none")
                .transition()
                .style("opacity", 0);
            window.removeEventListener("click", handleClickAway, true);
            window.removeEventListener("touchstart", handleClickAway, true);
            document.removeEventListener("keyup", handleKeyPress);
            return;
        }
        // otherwise, attached close handlers
        else {
            window.addEventListener("click", handleClickAway, true);
            window.addEventListener("touchstart", handleClickAway, true);
            document.addEventListener("keyup", handleKeyPress);
        }

        d3.select(state.selector)
            .style("pointer-events", "auto")
            .transition().duration(500)
            .style("opacity", 1);

        d3.select(state.selector)
            .style("width", state.width + "px");

        // populate and measure the title container
        titleContainer
            .style("margin-left", `${margin.left - 5}px`)
            .text(state.indicators[state.activeIndicator].shortName);
        const titleHeight = $(titleContainer.node()).outerHeight();


        const height = (state.height || state.width) - titleHeight;

        vizWidth = $(state.selector).width() - margin.left - margin.right,
        vizHeight = height - margin.top - margin.bottom;

        svg.attr("width", vizWidth + margin.left + margin.right)
            .attr("height", vizHeight + margin.top + margin.bottom);

        xScale.rangeBands([4, vizWidth - 50]); // leave space for first and last bars
        yScale.range([vizHeight, 0]);

        yAxis.tickSize(-xScale.rangeExtent()[1]);

        const indicatorData = R.values(state.cleanData[state.activeIndicator])
            .filter(d => d.locId !== 0)
            .filter(d => !R.isNil(core.latestYearValue(d)))
            .sort(valueComparator);

        const dataEndPoints = [R.head(indicatorData), R.last(indicatorData)];

        const maxYVal = d3.max(indicatorData, core.latestYearValue);

        xScale.domain(indicatorData.map(d => d.locId));
        yScale.domain([0, maxYVal]).nice();

        xAxis.tickValues(dataEndPoints.map(d => d.locId));
        yAxis.tickValues(dataEndPoints.map(core.latestYearValue));


        // better/worse label
        g.select(".better-worse")
            .attr("x", vizWidth)
            .attr("y", vizHeight)
            .style({
                "text-anchor": "end"
            })
            .text(core.lowerIsBetter(state.indicators[state.activeIndicator]) ? "Better" : "Worse");

        var bars = g.selectAll("rect.bar").data(indicatorData, (d, i) => i);

        bars.enter().append("rect").attr("class", "bar")
            .attr("height", 0)
            .attr("y", vizHeight);

        bars.exit().remove();

        bars
            .classed("highlight", d => isExtreme(d.locId))
            .attr("rx", 2)
            .attr("ry", 2)
            .attr("x", d => positionX(d.locId))
            .attr("width", d => xScale.rangeBand() + (isExtreme(d.locId)? 2 : 0))
            .attr("y", d => yScale(core.latestYearValue(d)))
            .attr("height", d => vizHeight - yScale(core.latestYearValue(d)));

        moveToFront(bars.filter(d => isExtreme(d.locId)));

        var xAxisElement = g.select("g.axis.x");

        if (xAxisElement.empty()) {
            xAxisElement = g.append("g").attr("class", "x axis");
        }

        xAxisElement
            .attr("transform", "translate(0," + (vizHeight) + ")")
            .call(xAxis)
            .selectAll("text")
            .style("text-anchor", (d, i) => i === 0? "start": "end");

        // Bump the tick values to overcome our offset scale
        xAxisElement.selectAll("text").filter((d, i) => i === 0)
            .attr("transform", "translate(-7, 0)");

        xAxisElement.selectAll("text").filter((d, i) => i === 1)
            .attr("transform", "translate(5, 0)");

        var yAxisElement = g.select("g.axis.y");

        if (yAxisElement.empty()) {
            yAxisElement = g.append("g").attr("class", "y axis");
        }

        yAxisElement
            .call(yAxis);

        var axisArrows = g.select("g.axis.arrows");

        if(axisArrows.empty()){
            axisArrows = g.append("g")
                .attr("class", "axis arrows");

            axisArrows
                .append("path")
                .attr("marker-end", "url(#arrow-spread)")
                .attr("marker-start", "url(#arrow-spread)");
        }

        // Fine tune the axis ticks to only point to the high low bars
        yAxisElement.selectAll("line").filter((d, i) => i === 0)
            .attr("x1", 0)
            .attr("x2", 0);

        yAxisElement.selectAll("line").filter((d, i) => i === 1)
            .attr("x1", 0)
            .attr("x2", xScale(R.last(indicatorData).locId));

        axisArrows.select("path")
            .attr("d", axisArrowsPath);


        // Difference note
        var differenceNote = g.select("g.difference");

        if(differenceNote.empty()){
            differenceNote = g.append("g").attr("class", "difference");
            differenceNote.append("path");
            differenceNote.append("text");
        }

        differenceNote.select("path")
            .attr("d", differencePath(dataEndPoints));

        var endCaps = differenceNote.selectAll("line.endcap").data(dataEndPoints);
        endCaps.enter().append("line").attr("class", "endcap");
        endCaps.exit().remove();

        endCaps
            .attr("x1", xScale.rangeExtent()[1] + differenceNoteOffset - 5)
            .attr("x2", xScale.rangeExtent()[1] + differenceNoteOffset + 6)
            .attr("y1", d => yScale(core.latestYearValue(d)))
            .attr("y2", d => yScale(core.latestYearValue(d)));

        const dataExtents = dataEndPoints.map(core.latestYearValue);

        var differenceMultiple = core.calculateDifferenceDescription(dataExtents);

        differenceNote.select("text")
            .attr("x", xScale.rangeExtent()[1] + differenceNoteOffset - 10)
            .attr("y", yScale(dataExtents[0]) + 20)
            .text(differenceMultiple + " difference");
    }

    update();

    // external api
    return {
        setState,
        setActiveIndicator: (ind) => setState({activeIndicator: ind})
    };
}
